// src/pages/achats/Fournisseurs.jsx
import React, { useState, useEffect, useMemo } from "react";
import {
    Card, Table, Row, Col, Form, Button, Badge,
    Spinner, Alert, Tooltip, OverlayTrigger, Modal, Pagination, InputGroup
} from "react-bootstrap";
import { Link, useNavigate } from "react-router-dom";
import Footer from "../layouts/Footer";
import Header from "../layouts/Header";
import { formatCurrency, formatDate } from "../utils/formatters";
import { API_BASE_URL } from "../constants/api";
import { COLORS } from "../constants/colors";

export default function Fournisseurs() {
    const navigate = useNavigate();
    const [fournisseurs, setFournisseurs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [filterText, setFilterText] = useState('');
    const [stats, setStats] = useState(null);
    const [sortConfig, setSortConfig] = useState({ key: 'raison_sociale', direction: 'asc' });
    const [showEvalModal, setShowEvalModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [selectedFournisseur, setSelectedFournisseur] = useState(null);
    const [fournisseurToDelete, setFournisseurToDelete] = useState(null);
    const [evaluation, setEvaluation] = useState({
        note: 5,
        critere_delai: 5,
        critere_qualite: 5,
        critere_prix: 5,
        commentaire: ''
    });
    const [evalLoading, setEvalLoading] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [toast, setToast] = useState({ show: false, type: 'success', title: '', message: '' });
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage] = useState(10);

    /* ============================================================
       TOAST
       ============================================================ */
    const showToast = (type, title, message) => setToast({ show: true, type, title, message });
    const hideToast = () => setToast({ show: false, type: 'success', title: '', message: '' });

    /* ============================================================
       FETCH
       ============================================================ */
    useEffect(() => {
        fetchFournisseurs();
        fetchStats();
    }, []);

    const fetchFournisseurs = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem("token");
            const response = await fetch(
                `${API_BASE_URL}/fournisseurs.php`,
                { headers: { 'Authorization': `Bearer ${token}` } }
            );
            if (!response.ok) throw new Error("Erreur chargement");
            const result = await response.json();
            setFournisseurs(result.data || []);
            setCurrentPage(1);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const fetchStats = async () => {
        try {
            const token = localStorage.getItem("token");
            const response = await fetch(
                `${API_BASE_URL}/fournisseurs.php?action=stats`,
                { headers: { 'Authorization': `Bearer ${token}` } }
            );
            if (response.ok) {
                const result = await response.json();
                setStats(result.data);
            }
        } catch (err) {
            console.error("Erreur stats", err);
        }
    };

    /* ============================================================
       ACTIONS
       ============================================================ */
    const handleDelete = async () => {
        if (!fournisseurToDelete) return;
        setDeleting(true);
        try {
            const token = localStorage.getItem("token");
            const response = await fetch(
                `${API_BASE_URL}/fournisseurs.php?id=${fournisseurToDelete.id_fournisseur}`,
                {
                    method: 'DELETE',
                    headers: { 'Authorization': `Bearer ${token}` }
                }
            );
            const result = await response.json();
            if (result.success) {
                setShowDeleteModal(false);
                setFournisseurToDelete(null);
                fetchFournisseurs();
                fetchStats();
                showToast('success', 'Fournisseur supprimé', 'Le fournisseur a été supprimé avec succès');
            } else {
                showToast('error', 'Erreur', result.message);
            }
        } catch (err) {
            showToast('error', 'Erreur', "Erreur lors de la suppression");
        } finally {
            setDeleting(false);
        }
    };

    const handleEvaluer = (fournisseur) => {
        setSelectedFournisseur(fournisseur);
        setEvaluation({
            note: 5,
            critere_delai: 5,
            critere_qualite: 5,
            critere_prix: 5,
            commentaire: ''
        });
        setShowEvalModal(true);
    };

    const openDeleteModal = (fournisseur) => {
        setFournisseurToDelete(fournisseur);
        setShowDeleteModal(true);
    };

    const submitEvaluation = async () => {
        setEvalLoading(true);
        try {
            const token = localStorage.getItem("token");
            const user = JSON.parse(localStorage.getItem("user") || "{}");
            const response = await fetch(`${API_BASE_URL}/fournisseurs.php`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    action: 'evaluer',
                    id_fournisseur: selectedFournisseur.id_fournisseur,
                    ...evaluation,
                    id_utilisateur: user.id_utilisateur || 1
                })
            });
            const result = await response.json();
            if (result.success) {
                setShowEvalModal(false);
                showToast('success', 'Évaluation enregistrée', 'L\'évaluation a été enregistrée avec succès');
                fetchFournisseurs();
                fetchStats();
            } else {
                showToast('error', 'Erreur', result.message);
            }
        } catch (err) {
            showToast('error', 'Erreur', "Erreur lors de l'évaluation");
        } finally {
            setEvalLoading(false);
        }
    };

    /* ============================================================
       HELPERS
       ============================================================ */
    const getNoteStars = (note) => {
        const noteValue = note ? Math.round(parseFloat(note)) : 0;
        return Array.from({ length: 5 }, (_, i) => (
            <i
                key={i}
                className={`ri-star-${i < noteValue ? 'fill' : 'line'}`}
                style={{ fontSize: '0.85rem', color: '#f59e0b' }}
            ></i>
        ));
    };

    const getStatutBadge = (fournisseur) => {
        if (fournisseur.nb_commandes > 0) {
            return (
                <span className="status-badge-charte" style={{ background: `linear-gradient(135deg, ${COLORS.success}, #059669)` }}>
                    <i className="ri-checkbox-circle-line"></i>
                    Actif
                </span>
            );
        }
        return (
            <span className="status-badge-charte" style={{ background: `linear-gradient(135deg, #64748b, #475569)` }}>
                <i className="ri-pause-circle-line"></i>
                Inactif
            </span>
        );
    };

    /* ============================================================
       FILTRES / TRI / PAGINATION
       ============================================================ */
    const filteredFournisseurs = useMemo(() => {
        return fournisseurs
            .filter(f =>
                f.raison_sociale?.toLowerCase().includes(filterText.toLowerCase()) ||
                f.code_fournisseur?.toLowerCase().includes(filterText.toLowerCase()) ||
                f.email?.toLowerCase().includes(filterText.toLowerCase()) ||
                f.telephone?.includes(filterText)
            )
            .sort((a, b) => {
                if (sortConfig.key === 'raison_sociale') {
                    return sortConfig.direction === 'asc'
                        ? a.raison_sociale?.localeCompare(b.raison_sociale)
                        : b.raison_sociale?.localeCompare(a.raison_sociale);
                }
                if (sortConfig.key === 'total_achats') {
                    return sortConfig.direction === 'asc'
                        ? (a.total_achats || 0) - (b.total_achats || 0)
                        : (b.total_achats || 0) - (a.total_achats || 0);
                }
                return 0;
            });
    }, [fournisseurs, filterText, sortConfig]);

    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentItems = filteredFournisseurs.slice(indexOfFirstItem, indexOfLastItem);
    const totalPages = Math.ceil(filteredFournisseurs.length / itemsPerPage);

    const handlePageChange = (pageNumber) => {
        setCurrentPage(pageNumber);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const renderPagination = () => {
        let items = [];
        const maxVisible = 5;
        let startPage = Math.max(1, currentPage - Math.floor(maxVisible / 2));
        let endPage = Math.min(totalPages, startPage + maxVisible - 1);
        if (endPage - startPage + 1 < maxVisible) startPage = Math.max(1, endPage - maxVisible + 1);

        if (startPage > 1) {
            items.push(<Pagination.Item key={1} onClick={() => handlePageChange(1)}>1</Pagination.Item>);
            if (startPage > 2) items.push(<Pagination.Ellipsis key="ellipsis-start" disabled />);
        }
        for (let number = startPage; number <= endPage; number++) {
            items.push(
                <Pagination.Item key={number} active={number === currentPage} onClick={() => handlePageChange(number)}>
                    {number}
                </Pagination.Item>
            );
        }
        if (endPage < totalPages) {
            if (endPage < totalPages - 1) items.push(<Pagination.Ellipsis key="ellipsis-end" disabled />);
            items.push(<Pagination.Item key={totalPages} onClick={() => handlePageChange(totalPages)}>{totalPages}</Pagination.Item>);
        }
        return items;
    };

    const handleSort = (key) => {
        setSortConfig(prev => ({
            key,
            direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
        }));
    };

    const SortableHeader = ({ field, children, className = '' }) => (
        <th className={`sortable-th-charte ${className}`} onClick={() => handleSort(field)}>
            <div className="d-flex align-items-center gap-1">
                {children}
                {sortConfig.key === field && (
                    <i className={`ri-arrow-${sortConfig.direction === 'asc' ? 'up' : 'down'}-s-line`}></i>
                )}
            </div>
        </th>
    );

    const ActionButton = ({ icon, onClick, tooltip, color }) => (
        <OverlayTrigger placement="top" overlay={<Tooltip>{tooltip}</Tooltip>}>
            <button
                className="action-btn-charte"
                onClick={onClick}
                style={{ '--btn-color': color || COLORS.primary }}
            >
                <i className={icon}></i>
            </button>
        </OverlayTrigger>
    );

    /* ============================================================
       LOADING
       ============================================================ */
   

      if (loading && !fournisseurs.length) {
               
                       return (
                           <>
                               <Header />
                               <div className="main main-app p-3 p-lg-4" style={{ background: '#f8fafc', minHeight: '100vh' }}>
                                   <div className="d-flex justify-content-center align-items-center" style={{ minHeight: 'calc(100vh - 100px)' }}>
                                       <div className="text-center" style={{ maxWidth: '400px' }}>
                                           <div className="loading-ring-wrapper mb-4">
                                               <div className="loading-ring"></div>
                                               <div className="loading-ring-inner"></div>
                                               <i className="ri-loader-4-fill loading-icon"></i>
                                           </div>
                                           <h5 className="fw-bold" style={{ color: COLORS.primaryDark, letterSpacing: '0.3px' }}>
                                               Chargement des données...
                                           </h5>
                                           <p className="text-muted small mb-0">Veuillez patienter quelques instants...</p>
                                           <div className="mx-auto mt-4" style={{ width: '80%' }}>
                                               <div className="progress" style={{ height: '3px', background: '#e9ecef', borderRadius: '10px', overflow: 'hidden' }}>
                                                   <div
                                                       className="progress-bar progress-bar-striped progress-bar-animated"
                                                       style={{
                                                           width: '100%',
                                                           background: `linear-gradient(90deg, ${COLORS.primary}, ${COLORS.secondary}, ${COLORS.primary})`,
                                                           backgroundSize: '200% 100%',
                                                           animation: 'shimmer 1.5s linear infinite'
                                                       }}
                                                   />
                                               </div>
                                           </div>
                                       </div>
                                   </div>
                               </div>
                               <Footer />
                               <style>{`
                                   .loading-ring-wrapper { position: relative; width: 80px; height: 80px; margin: 0 auto; }
                                   .loading-ring { position: absolute; top: 0; left: 0; width: 100%; height: 100%; border-radius: 50%; border: 4px solid rgba(33,172,205,0.15); border-top-color: ${COLORS.primary}; animation: spin 1s linear infinite; }
                                   .loading-ring-inner { position: absolute; top: 8px; left: 8px; right: 8px; bottom: 8px; border-radius: 50%; border: 3px solid rgba(33,172,205,0.08); border-bottom-color: ${COLORS.secondary}; animation: spin 0.8s linear infinite reverse; }
                                   .loading-icon { position: absolute; top: 50%; left: 50%; transform: translate(-50%, -50%); font-size: 28px; color: ${COLORS.primary}; animation: pulse-icon 1.2s ease-in-out infinite; }
                                   @keyframes spin { to { transform: rotate(360deg); } }
                                   @keyframes pulse-icon { 0%,100% { transform: translate(-50%,-50%) scale(1); opacity:1; } 50% { transform: translate(-50%,-50%) scale(1.15); opacity:0.6; } }
                                   @keyframes shimmer { 0% { background-position: 200% 0; } 100% { background-position: -200% 0; } }
                               `}</style>
                           </>
                       );
                   }

    if (error && !fournisseurs.length) {
        return (
            <>
                <Header />
                <div className="main main-app p-3 p-lg-4" style={{ background: '#f8fafc', minHeight: '100vh' }}>
                    <Alert className="alert-error-charte">
                        <div className="d-flex align-items-center">
                            <div className="alert-error-icon"><i className="ri-error-warning-line"></i></div>
                            <div className="ms-3 flex-grow-1">
                                <h6 className="mb-1 fw-bold">Erreur de chargement</h6>
                                <p className="mb-0 small">{error}</p>
                            </div>
                            <Button onClick={fetchFournisseurs} className="btn-modern-outline">
                                <i className="ri-refresh-line me-1"></i>Réessayer
                            </Button>
                        </div>
                    </Alert>
                </div>
                <Footer />
            </>
        );
    }

    /* ============================================================
       RENDU PRINCIPAL
       ============================================================ */
    return (
        <>
            <Header />
            <div className="main main-app p-3 p-lg-4" style={{ background: '#f8fafc', minHeight: '100vh' }}>

                {/* ===== TOAST ===== */}
                {toast.show && (
                    <div className="toast-notification-fixed">
                        <div className="toast-modern">
                            <div className="toast-header-custom" style={{
                                background: toast.type === 'success'
                                    ? `linear-gradient(135deg, ${COLORS.success}, #059669)`
                                    : `linear-gradient(135deg, ${COLORS.danger}, #dc2626)`
                            }}>
                                <div className="d-flex align-items-center gap-2">
                                    <i className={`${toast.type === 'success' ? 'ri-checkbox-circle-fill' : 'ri-error-warning-fill'} text-white`} style={{ fontSize: '1.5rem' }}></i>
                                    <strong className="text-white fs-6">{toast.title}</strong>
                                </div>
                                <button onClick={hideToast} className="toast-close-btn">
                                    <i className="ri-close-line fs-5"></i>
                                </button>
                            </div>
                            <div className="toast-body-custom">
                                <p className="mb-0">{toast.message}</p>
                            </div>
                            <div className="toast-progress" style={{
                                background: toast.type === 'success' ? COLORS.success : COLORS.danger,
                                animation: 'shrink 3000ms linear forwards'
                            }}></div>
                        </div>
                    </div>
                )}

                <div className="container-fluid">

                    {/* ===== EN-TÊTE MODERNE ===== */}
                    <div className="header-modern mb-4">
                        <div className="header-left">
                            <div className="header-icon-wrapper">
                                <i className="ri-truck-line"></i>
                            </div>
                            <div>
                                <h5 className="header-title">Fournisseurs</h5>
                                <div className="d-flex flex-wrap gap-2 mt-1">
                                    <span className="badge-modern">
                                        <i className="ri-truck-line"></i>
                                        {stats ? `${stats.total_fournisseurs || 0} fournisseurs` : '0 fournisseur'}
                                    </span>
                                    <span className="badge-modern" style={{ background: `${COLORS.success}15`, color: COLORS.success }}>
                                        <i className="ri-checkbox-circle-line"></i>
                                        {stats?.fournisseurs_actifs || 0} actifs
                                    </span>
                                    <span className="badge-modern" style={{ background: `${COLORS.warning}15`, color: COLORS.warning }}>
                                        <i className="ri-star-line"></i>
                                        Note {stats?.note_moyenne ? parseFloat(stats.note_moyenne).toFixed(1) : 'N/A'}/5
                                    </span>
                                </div>
                            </div>
                        </div>
                        <div className="d-flex gap-2 flex-wrap">
                            <Button className="btn-modern-outline" onClick={fetchFournisseurs} disabled={loading}>
                                <i className={`ri-refresh-line ${loading ? 'spin-icon' : ''}`}></i> Actualiser
                            </Button>
                            <Button className="btn-modern-primary" onClick={() => navigate('/achats/fournisseursAjouter')}>
                                <i className="ri-add-line"></i> Nouveau fournisseur
                            </Button>
                        </div>
                    </div>

                    {/* ===== KPI CARDS ===== */}
                    {stats && (
                        <Row className="g-3 mb-4">
                            <Col sm="6" xl="4">
                                <div className="kpi-charte kpi-primary">
                                    <div className="kpi-icon-charte"><i className="ri-truck-line"></i></div>
                                    <div className="kpi-content-charte">
                                        <span className="kpi-label-charte">Total fournisseurs</span>
                                        <h3 className="kpi-value-charte">{stats.total_fournisseurs || 0}</h3>
                                        <span className="kpi-unit-charte">+{stats.nouveaux_ajd || 0} aujourd'hui</span>
                                    </div>
                                </div>
                            </Col>
                            <Col sm="6" xl="4">
                                <div className="kpi-charte kpi-success">
                                    <div className="kpi-icon-charte"><i className="ri-checkbox-circle-line"></i></div>
                                    <div className="kpi-content-charte">
                                        <span className="kpi-label-charte">Fournisseurs actifs</span>
                                        <h3 className="kpi-value-charte">{stats.fournisseurs_actifs || 0}</h3>
                                        <span className="kpi-unit-charte">avec commandes récentes</span>
                                    </div>
                                </div>
                            </Col>
                            <Col sm="6" xl="4">
                                <div className="kpi-charte kpi-warning">
                                    <div className="kpi-icon-charte"><i className="ri-star-line"></i></div>
                                    <div className="kpi-content-charte">
                                        <span className="kpi-label-charte">Note moyenne</span>
                                        <h3 className="kpi-value-charte">
                                            {stats.note_moyenne ? parseFloat(stats.note_moyenne).toFixed(1) : 'N/A'}
                                        </h3>
                                        <span className="kpi-unit-charte">/5 satisfaction</span>
                                    </div>
                                </div>
                            </Col>
                        </Row>
                    )}

                    {/* ===== FILTRES ===== */}
                    <Card className="filter-card-charte mb-4">
                        <Card.Body className="py-3 px-4">
                            <Row className="align-items-center g-3">
                                <Col md="8">
                                    <div className="search-box-charte">
                                        <i className="ri-search-line search-icon-charte"></i>
                                        <Form.Control
                                            type="text"
                                            placeholder="Rechercher par nom, code, email, téléphone..."
                                            value={filterText}
                                            onChange={(e) => setFilterText(e.target.value)}
                                            className="search-input-charte"
                                        />
                                    </div>
                                </Col>
                                <Col md="4" className="text-end">
                                    <span className="count-badge-charte">
                                        <i className="ri-stack-line"></i>
                                        {filteredFournisseurs.length} fournisseur(s)
                                    </span>
                                </Col>
                            </Row>
                        </Card.Body>
                    </Card>

                    {/* ===== TABLEAU ===== */}
                    <Card className="border-0 shadow-sm rounded-3 overflow-hidden">
                        <Card.Header className="card-header-gradient">
                            <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                                <div className="d-flex align-items-center gap-2">
                                    <i className="ri-truck-line text-white fs-5"></i>
                                    <span className="fw-semibold text-white">Liste des fournisseurs</span>
                                    <span className="badge-header-light">{filteredFournisseurs.length}</span>
                                </div>
                            </div>
                        </Card.Header>
                        <Card.Body className="p-0">
                            <div className="table-responsive">
                                <Table hover className="align-middle mb-0 table-charte">
                                    <thead>
                                        <tr>
                                            <SortableHeader field="raison_sociale" className="ps-4">Fournisseur</SortableHeader>
                                            <th>Contact</th>
                                            <SortableHeader field="total_achats" className="text-end">Total achats</SortableHeader>
                                            <th>Dernière commande</th>
                                            <th className="text-center">Note</th>
                                            <th className="text-center">Statut</th>
                                            <th className="text-center pe-4">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {currentItems.length > 0 ? (
                                            currentItems.map((f) => (
                                                <tr key={f.id_fournisseur}>
                                                    <td className="ps-4">
                                                        <Link
                                                            to={`/achats/fournisseurs/${f.id_fournisseur}`}
                                                            className="table-link-charte"
                                                        >
                                                            {f.raison_sociale}
                                                        </Link>
                                                        <div className="small text-muted" style={{ fontSize: '0.7rem' }}>
                                                            <i className="ri-barcode-line me-1"></i>
                                                            {f.code_fournisseur}
                                                        </div>
                                                    </td>
                                                    <td>
                                                        <div className="d-flex flex-column gap-1">
                                                            {f.telephone && (
                                                                <small className="text-muted">
                                                                    <i className="ri-phone-line me-1"></i>
                                                                    {f.telephone}
                                                                </small>
                                                            )}
                                                            {!f.telephone && <span className="text-muted">—</span>}
                                                        </div>
                                                    </td>
                                                    <td className="text-end">
                                                        <span className="fw-bold" style={{ color: COLORS.primary }}>
                                                            {formatCurrency(f.total_achats || 0)}
                                                        </span>
                                                    </td>
                                                    <td>
                                                        {f.derniere_commande ? (
                                                            <small className="text-muted">
                                                                <i className="ri-calendar-line me-1"></i>
                                                                {formatDate(f.derniere_commande)}
                                                            </small>
                                                        ) : (
                                                            <span className="text-muted">—</span>
                                                        )}
                                                    </td>
                                                    <td className="text-center">
                                                        <div className="d-flex align-items-center justify-content-center gap-1">
                                                            {getNoteStars(f.note_moyenne)}
                                                            {f.note_moyenne && (
                                                                <small className="text-muted ms-1">
                                                                    ({parseFloat(f.note_moyenne).toFixed(1)})
                                                                </small>
                                                            )}
                                                        </div>
                                                    </td>
                                                    <td className="text-center">{getStatutBadge(f)}</td>
                                                    <td className="text-center pe-4">
                                                        <div className="d-flex gap-2 justify-content-center">
                                                            <ActionButton
                                                                icon="ri-eye-line"
                                                                onClick={() => navigate(`/achats/fournisseurs/${f.id_fournisseur}`)}
                                                                tooltip="Voir détails"
                                                            />
                                                            <ActionButton
                                                                icon="ri-pencil-line"
                                                                onClick={() => navigate(`/achats/fournisseurs/modifier/${f.id_fournisseur}`)}
                                                                tooltip="Modifier"
                                                                color={COLORS.info}
                                                            />
                                                            <ActionButton
                                                                icon="ri-star-line"
                                                                onClick={() => handleEvaluer(f)}
                                                                tooltip="Évaluer"
                                                                color={COLORS.warning}
                                                            />
                                                            <ActionButton
                                                                icon="ri-delete-bin-line"
                                                                onClick={() => openDeleteModal(f)}
                                                                tooltip="Supprimer"
                                                                color={COLORS.danger}
                                                            />
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))
                                        ) : (
                                            <tr>
                                                <td colSpan="7">
                                                    <div className="empty-state-charte">
                                                        <div className="empty-state-icon-charte">
                                                            <i className="ri-truck-line"></i>
                                                        </div>
                                                        <h6 className="fw-semibold mb-1">
                                                            {filterText ? 'Aucun résultat' : 'Aucun fournisseur trouvé'}
                                                        </h6>
                                                        <p className="text-muted small mb-3">
                                                            {filterText
                                                                ? "Aucun résultat ne correspond à votre recherche"
                                                                : "Commencez par ajouter votre premier fournisseur"}
                                                        </p>
                                                        {filterText ? (
                                                            <Button
                                                                onClick={() => setFilterText('')}
                                                                className="btn-modern-outline"
                                                                size="sm"
                                                            >
                                                                <i className="ri-refresh-line me-1"></i>Réinitialiser
                                                            </Button>
                                                        ) : (
                                                            <Button
                                                                onClick={() => navigate('/achats/fournisseursAjouter')}
                                                                className="btn-modern-primary"
                                                                size="sm"
                                                            >
                                                                <i className="ri-add-line me-1"></i>Ajouter un fournisseur
                                                            </Button>
                                                        )}
                                                    </div>
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </Table>
                            </div>

                            {/* Pagination */}
                            {totalPages > 1 && (
                                <div className="pagination-wrapper-charte">
                                    <div className="pagination-info-charte">
                                        Affichage de <strong>{indexOfFirstItem + 1}</strong> à <strong>{Math.min(indexOfLastItem, filteredFournisseurs.length)}</strong> sur <strong>{filteredFournisseurs.length}</strong> fournisseur(s)
                                    </div>
                                    <Pagination className="pagination-charte mb-0">
                                        <Pagination.Prev onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1} />
                                        {renderPagination()}
                                        <Pagination.Next onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === totalPages} />
                                    </Pagination>
                                </div>
                            )}
                        </Card.Body>
                    </Card>
                </div>
                <Footer />
            </div>

            {/* ===== MODAL ÉVALUATION ===== */}
            <Modal show={showEvalModal} onHide={() => setShowEvalModal(false)} centered className="modal-charte">
                <Modal.Header closeButton className="modal-header-charte" style={{ background: `linear-gradient(135deg, ${COLORS.warning}, #d97706)` }}>
                    <div className="d-flex align-items-center gap-3">
                        <div className="modal-header-icon"><i className="ri-star-line"></i></div>
                        <div>
                            <Modal.Title className="fs-5 fw-bold text-white mb-0">
                                Évaluer {selectedFournisseur?.raison_sociale}
                            </Modal.Title>
                            <p className="text-white-50 mb-0 small">Notez la performance du fournisseur</p>
                        </div>
                    </div>
                </Modal.Header>
                <Modal.Body className="p-4">
                    <Form>
                        <Form.Group className="mb-4">
                            <Form.Label className="form-label-modern text-center d-block">Note globale</Form.Label>
                            <div className="d-flex justify-content-center gap-2">
                                {[1, 2, 3, 4, 5].map(n => (
                                    <button
                                        key={n}
                                        type="button"
                                        onClick={() => setEvaluation({ ...evaluation, note: n })}
                                        className={`note-btn-charte ${evaluation.note >= n ? 'active' : ''}`}
                                    >
                                        {n}
                                    </button>
                                ))}
                            </div>
                        </Form.Group>

                        <Row className="g-3 mb-3">
                            <Col md={4}>
                                <Form.Group>
                                    <Form.Label className="form-label-modern">Délais</Form.Label>
                                    <InputGroup className="input-modern">
                                        <InputGroup.Text><i className="ri-timer-line"></i></InputGroup.Text>
                                        <Form.Select
                                            value={evaluation.critere_delai}
                                            onChange={(e) => setEvaluation({ ...evaluation, critere_delai: parseInt(e.target.value) })}
                                        >
                                            <option value="5">Excellent</option>
                                            <option value="4">Bon</option>
                                            <option value="3">Moyen</option>
                                            <option value="2">Médiocre</option>
                                            <option value="1">Mauvais</option>
                                        </Form.Select>
                                    </InputGroup>
                                </Form.Group>
                            </Col>
                            <Col md={4}>
                                <Form.Group>
                                    <Form.Label className="form-label-modern">Qualité</Form.Label>
                                    <InputGroup className="input-modern">
                                        <InputGroup.Text><i className="ri-shield-check-line"></i></InputGroup.Text>
                                        <Form.Select
                                            value={evaluation.critere_qualite}
                                            onChange={(e) => setEvaluation({ ...evaluation, critere_qualite: parseInt(e.target.value) })}
                                        >
                                            <option value="5">Excellent</option>
                                            <option value="4">Bon</option>
                                            <option value="3">Moyen</option>
                                            <option value="2">Médiocre</option>
                                            <option value="1">Mauvais</option>
                                        </Form.Select>
                                    </InputGroup>
                                </Form.Group>
                            </Col>
                            <Col md={4}>
                                <Form.Group>
                                    <Form.Label className="form-label-modern">Prix</Form.Label>
                                    <InputGroup className="input-modern">
                                        <InputGroup.Text><i className="ri-money-euro-circle-line"></i></InputGroup.Text>
                                        <Form.Select
                                            value={evaluation.critere_prix}
                                            onChange={(e) => setEvaluation({ ...evaluation, critere_prix: parseInt(e.target.value) })}
                                        >
                                            <option value="5">Très compétitif</option>
                                            <option value="4">Compétitif</option>
                                            <option value="3">Dans la moyenne</option>
                                            <option value="2">Élevé</option>
                                            <option value="1">Très élevé</option>
                                        </Form.Select>
                                    </InputGroup>
                                </Form.Group>
                            </Col>
                        </Row>

                        <Form.Group className="mb-0">
                            <Form.Label className="form-label-modern">Commentaire</Form.Label>
                            <Form.Control
                                as="textarea"
                                rows={3}
                                value={evaluation.commentaire}
                                onChange={(e) => setEvaluation({ ...evaluation, commentaire: e.target.value })}
                                placeholder="Votre avis sur ce fournisseur..."
                                className="textarea-modern"
                            />
                        </Form.Group>
                    </Form>
                </Modal.Body>
                <Modal.Footer className="border-0 pb-4 px-4 pt-0">
                    <Button onClick={() => setShowEvalModal(false)} className="btn-modern-outline" disabled={evalLoading}>
                        Annuler
                    </Button>
                    <Button onClick={submitEvaluation} disabled={evalLoading} className="btn-modern-warning">
                        {evalLoading ? (
                            <><Spinner size="sm" className="me-2" />Enregistrement...</>
                        ) : (
                            <><i className="ri-star-line me-2"></i>Évaluer</>
                        )}
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* ===== MODAL SUPPRESSION ===== */}
            <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} centered className="modal-charte">
                <Modal.Header closeButton className="modal-header-charte" style={{ background: `linear-gradient(135deg, ${COLORS.danger}, #dc2626)` }}>
                    <div className="d-flex align-items-center gap-3">
                        <div className="modal-header-icon"><i className="ri-delete-bin-line"></i></div>
                        <div>
                            <Modal.Title className="fs-5 fw-bold text-white mb-0">Confirmer la suppression</Modal.Title>
                            <p className="text-white-50 mb-0 small">Cette action est irréversible</p>
                        </div>
                    </div>
                </Modal.Header>
                <Modal.Body className="p-4">
                    <p className="text-center text-muted mb-3">
                        Êtes-vous sûr de vouloir supprimer ce fournisseur ?
                    </p>
                    <div className="summary-card-charte mb-3">
                        <div className="d-flex align-items-center gap-3">
                            <div className="supplier-icon-charte">
                                <i className="ri-truck-line"></i>
                            </div>
                            <div>
                                <div className="fw-bold" style={{ color: COLORS.primaryDark }}>
                                    {fournisseurToDelete?.raison_sociale}
                                </div>
                                <small className="text-muted">
                                    <i className="ri-barcode-line me-1"></i>
                                    Code: {fournisseurToDelete?.code_fournisseur}
                                </small>
                            </div>
                        </div>
                    </div>
                    <div className="alert-error-charte mb-0">
                        <div className="d-flex align-items-center">
                            <div className="alert-error-icon"><i className="ri-alert-line"></i></div>
                            <div className="ms-3">
                                <strong>Attention</strong>
                                <p className="mb-0 small">Toutes les données associées seront définitivement supprimées</p>
                            </div>
                        </div>
                    </div>
                </Modal.Body>
                <Modal.Footer className="border-0 pb-4 px-4 pt-0">
                    <Button onClick={() => setShowDeleteModal(false)} className="btn-modern-outline" disabled={deleting}>
                        Annuler
                    </Button>
                    <Button onClick={handleDelete} disabled={deleting} className="btn-modern-danger">
                        {deleting ? (
                            <><Spinner size="sm" className="me-2" />Suppression...</>
                        ) : (
                            <><i className="ri-delete-bin-line me-2"></i>Confirmer</>
                        )}
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* ===== STYLES CHARTE ===== */}
            <style>{`
                :root {
                    --primary: ${COLORS.primary || '#295AA7'};
                    --primary-dark: ${COLORS.primaryDark || '#1e3f7a'};
                    --secondary: ${COLORS.secondary || '#21ACCD'};
                    --success: ${COLORS.success || '#10b981'};
                    --danger: ${COLORS.danger || '#ef4444'};
                    --warning: ${COLORS.warning || '#f59e0b'};
                    --info: ${COLORS.info || '#3b82f6'};
                }

                /* ===== EN-TÊTE ===== */
                .header-modern {
                    background: white; padding: 1.2rem 1.8rem;
                    box-shadow: 0 2px 8px rgba(0,0,0,0.04);
                    border-left: 4px solid var(--secondary);
                    border-radius: 12px;
                    display: flex; align-items: center; justify-content: space-between;
                    flex-wrap: wrap; gap: 1rem;
                }
                .header-left { display: flex; align-items: center; gap: 1rem; }
                .header-icon-wrapper {
                    width: 52px; height: 52px;
                    background: linear-gradient(135deg, var(--primary), var(--secondary));
                    display: flex; align-items: center; justify-content: center;
                    border-radius: 14px; color: white; font-size: 1.5rem;
                    box-shadow: 0 4px 12px rgba(41, 90, 167, 0.25);
                }
                .header-title { color: #1e293b; font-size: 1.3rem; font-weight: 700; margin: 0; }
                .badge-modern {
                    background: #f1f4f9; color: #475569;
                    padding: 0.3rem 0.8rem; border-radius: 50px;
                    font-size: 0.75rem; font-weight: 600;
                    display: inline-flex; align-items: center; gap: 6px;
                }
                .badge-modern i { color: var(--secondary); }
                .spin-icon { animation: spin 1s linear infinite; }

                /* ===== KPI ===== */
                .kpi-charte {
                    background: white; border-radius: 12px;
                    padding: 1.2rem;
                    display: flex; align-items: center; gap: 1rem;
                    box-shadow: 0 2px 8px rgba(0,0,0,0.04);
                    border-left: 4px solid;
                    transition: all 0.25s ease;
                    height: 100%;
                }
                .kpi-charte:hover {
                    transform: translateY(-3px);
                    box-shadow: 0 12px 28px rgba(0,0,0,0.08);
                }
                .kpi-primary { border-left-color: var(--primary); }
                .kpi-success { border-left-color: var(--success); }
                .kpi-warning { border-left-color: var(--warning); }
                .kpi-icon-charte {
                    width: 48px; height: 48px; border-radius: 12px;
                    display: flex; align-items: center; justify-content: center;
                    font-size: 1.4rem; flex-shrink: 0;
                }
                .kpi-primary .kpi-icon-charte { background: ${COLORS.primary}15; color: var(--primary); }
                .kpi-success .kpi-icon-charte { background: ${COLORS.success}15; color: var(--success); }
                .kpi-warning .kpi-icon-charte { background: ${COLORS.warning}15; color: var(--warning); }
                .kpi-content-charte { flex: 1; min-width: 0; }
                .kpi-label-charte {
                    font-size: 0.7rem; color: #94a3b8;
                    text-transform: uppercase; font-weight: 600;
                    letter-spacing: 0.5px;
                }
                .kpi-value-charte {
                    font-size: 1.35rem; font-weight: 700;
                    margin: 0.3rem 0; color: #1e293b;
                }
                .kpi-unit-charte { font-size: 0.7rem; color: #94a3b8; }

                /* ===== FILTRES ===== */
                .filter-card-charte {
                    background: linear-gradient(135deg, var(--primary-dark), var(--primary));
                    border-radius: 12px; border: none;
                    box-shadow: 0 8px 24px rgba(41,90,167,0.15);
                }
                .search-box-charte { position: relative; }
                .search-icon-charte {
                    position: absolute; top: 50%; left: 1rem;
                    transform: translateY(-50%);
                    color: var(--primary); font-size: 1rem;
                    z-index: 2; pointer-events: none;
                }
                .search-input-charte {
                    background: white !important;
                    border: none !important;
                    border-radius: 50px !important;
                    padding: 0.6rem 1rem 0.6rem 2.75rem !important;
                    font-size: 0.9rem;
                    box-shadow: 0 4px 12px rgba(0,0,0,0.08);
                }
                .search-input-charte:focus { box-shadow: 0 0 0 3px rgba(255,255,255,0.2) !important; }
                .count-badge-charte {
                    background: rgba(255,255,255,0.95); color: var(--primary);
                    padding: 0.6rem 1.25rem; border-radius: 50px;
                    font-size: 0.85rem; font-weight: 600;
                    display: inline-flex; align-items: center; gap: 6px;
                    box-shadow: 0 4px 12px rgba(0,0,0,0.08);
                }

                /* ===== HEADER GRADIENT ===== */
                .card-header-gradient {
                    background: linear-gradient(135deg, var(--primary) 0%, var(--primary-dark) 100%);
                    border-top-left-radius: 12px !important;
                    border-top-right-radius: 12px !important;
                    padding: 1rem 1.5rem;
                    border: none;
                }
                .badge-header-light {
                    background: rgba(255,255,255,0.2);
                    color: white;
                    border: 1px solid rgba(255,255,255,0.3);
                    border-radius: 50px;
                    padding: 0.25rem 0.75rem;
                    font-size: 0.75rem;
                    font-weight: 600;
                }

                /* ===== TABLE ===== */
                .table-charte thead th {
                    background: #f8fafc; color: #475569;
                    font-weight: 600; font-size: 0.7rem;
                    text-transform: uppercase; letter-spacing: 0.5px;
                    border-bottom: 2px solid #e2e8f0;
                    padding: 0.9rem 0.75rem;
                }
                .table-charte tbody td {
                    padding: 0.9rem 0.75rem;
                    vertical-align: middle;
                    border-bottom: 1px solid #f1f5f9;
                    font-size: 0.85rem;
                }
                .table-charte tbody tr { transition: background 0.15s ease; }
                .table-charte tbody tr:hover { background: #f8fafc; }
                .sortable-th-charte { cursor: pointer; user-select: none; transition: color 0.2s; }
                .sortable-th-charte:hover { color: var(--primary); }
                .table-link-charte {
                    color: var(--primary); text-decoration: none;
                    font-weight: 600;
                    transition: color 0.2s;
                }
                .table-link-charte:hover {
                    color: var(--primary-dark);
                    text-decoration: underline;
                }
                .status-badge-charte {
                    display: inline-flex; align-items: center; gap: 4px;
                    color: white; padding: 0.3rem 0.75rem;
                    border-radius: 50px; font-size: 0.7rem; font-weight: 600;
                    white-space: nowrap;
                }

                /* ===== ACTION BTN ===== */
                .action-btn-charte {
                    width: 34px; height: 34px; border-radius: 10px;
                    display: inline-flex; align-items: center; justify-content: center;
                    background: white; border: 1.5px solid #e2e8f0;
                    color: #64748b; font-size: 1rem;
                    transition: all 0.2s ease;
                    cursor: pointer;
                }
                .action-btn-charte:hover {
                    border-color: var(--btn-color); color: var(--btn-color);
                    transform: translateY(-2px);
                    box-shadow: 0 6px 16px rgba(41, 90, 167, 0.15);
                }

                /* ===== NOTE BTN ===== */
                .note-btn-charte {
                    width: 50px; height: 50px;
                    border-radius: 50%;
                    border: 2px solid var(--warning);
                    background: white;
                    color: var(--warning);
                    font-size: 1.2rem;
                    font-weight: 700;
                    cursor: pointer;
                    transition: all 0.2s ease;
                }
                .note-btn-charte:hover {
                    background: ${COLORS.warning}15;
                    transform: scale(1.05);
                }
                .note-btn-charte.active {
                    background: linear-gradient(135deg, var(--warning), #d97706);
                    color: white;
                    box-shadow: 0 4px 12px rgba(245, 158, 11, 0.3);
                }

                /* ===== EMPTY STATE ===== */
                .empty-state-charte { text-align: center; padding: 3rem 2rem; }
                .empty-state-icon-charte {
                    width: 72px; height: 72px;
                    background: linear-gradient(135deg, #f1f5f9, #e2e8f0);
                    border-radius: 50%;
                    display: flex; align-items: center; justify-content: center;
                    margin: 0 auto 1rem; font-size: 1.8rem; color: #94a3b8;
                }

                /* ===== PAGINATION ===== */
                .pagination-wrapper-charte {
                    display: flex; justify-content: space-between; align-items: center;
                    padding: 1rem 1.5rem;
                    border-top: 1px solid #f1f5f9;
                    flex-wrap: wrap; gap: 1rem;
                }
                .pagination-info-charte { font-size: 0.8rem; color: #64748b; }
                .pagination-charte { gap: 6px; }
                .pagination-charte .page-item .page-link {
                    border-radius: 10px;
                    border: 1.5px solid #e2e8f0;
                    color: #1e293b;
                    padding: 0.5rem 0.9rem;
                    font-weight: 500; font-size: 0.85rem;
                    transition: all 0.2s;
                }
                .pagination-charte .page-item.active .page-link {
                    background: linear-gradient(135deg, var(--primary), var(--primary-dark));
                    border-color: var(--primary);
                    color: white;
                    box-shadow: 0 4px 12px rgba(41,90,167,0.3);
                }
                .pagination-charte .page-item .page-link:hover {
                    background: #f1f5f9;
                    border-color: var(--secondary);
                    color: var(--primary);
                    transform: translateY(-1px);
                }

                /* ===== MODAL ===== */
                .modal-charte .modal-content {
                    border: none; border-radius: 20px; overflow: hidden;
                    box-shadow: 0 30px 80px rgba(0,0,0,0.15);
                }
                .modal-header-charte { padding: 1.25rem 1.5rem; border: none; }
                .modal-header-charte .btn-close { filter: brightness(0) invert(1); opacity: 0.8; }
                .modal-header-icon {
                    width: 48px; height: 48px; border-radius: 12px;
                    background: rgba(255,255,255,0.2);
                    display: flex; align-items: center; justify-content: center;
                    font-size: 1.5rem; color: white;
                }
                .summary-card-charte {
                    background: linear-gradient(135deg, #f8fafc, #ffffff);
                    padding: 1rem 1.25rem;
                    border-radius: 12px;
                    border: 1px solid #e2e8f0;
                }
                .supplier-icon-charte {
                    width: 52px; height: 52px; border-radius: 12px;
                    background: linear-gradient(135deg, var(--primary), var(--primary-dark));
                    display: flex; align-items: center; justify-content: center;
                    color: white; font-size: 1.4rem;
                    flex-shrink: 0;
                    box-shadow: 0 4px 12px rgba(41,90,167,0.25);
                }

                /* ===== INPUTS ===== */
                .form-label-modern {
                    font-weight: 600; color: #334155;
                    font-size: 0.85rem; margin-bottom: 0.4rem;
                }
                .input-modern .input-group-text {
                    background: #f8fafc; border: 2px solid #e2e8f0;
                    border-right: none; color: var(--primary);
                    border-radius: 10px 0 0 10px;
                }
                .input-modern .form-control,
                .input-modern .form-select {
                    border: 2px solid #e2e8f0; border-left: none;
                    border-radius: 0 10px 10px 0;
                    padding: 0.6rem 0.9rem; font-size: 0.9rem;
                    transition: all 0.2s ease;
                }
                .input-modern .form-control:focus,
                .input-modern .form-select:focus {
                    border-color: var(--secondary);
                    box-shadow: 0 0 0 3px rgba(33,172,205,0.1);
                }
                .textarea-modern {
                    border: 2px solid #e2e8f0;
                    border-radius: 10px;
                    padding: 0.8rem 0.9rem;
                    font-size: 0.9rem;
                    transition: all 0.2s ease;
                    resize: vertical;
                }
                .textarea-modern:focus {
                    border-color: var(--secondary);
                    box-shadow: 0 0 0 3px rgba(33,172,205,0.1);
                }

                /* ===== BOUTONS ===== */
                .btn-modern-outline {
                    border: 1.5px solid #d1d5db; background: white;
                    padding: 0.5rem 1.4rem; border-radius: 50px;
                    font-size: 0.85rem; font-weight: 500; color: #4b5563;
                    transition: all 0.2s ease;
                    display: inline-flex; align-items: center; gap: 6px;
                }
                .btn-modern-outline:hover {
                    border-color: var(--secondary); color: var(--secondary);
                    background: #f0fbff; transform: translateY(-1px);
                    box-shadow: 0 4px 8px rgba(33,172,205,0.15);
                }
                .btn-modern-primary, .btn-modern-danger, .btn-modern-warning {
                    border: none; color: white; padding: 0.5rem 1.6rem;
                    border-radius: 50px; font-weight: 600; font-size: 0.85rem;
                    transition: all 0.2s ease;
                    display: inline-flex; align-items: center; gap: 6px;
                }
                .btn-modern-primary {
                    background: linear-gradient(135deg, var(--primary), var(--primary-dark));
                    box-shadow: 0 4px 12px rgba(41,90,167,0.3);
                }
                .btn-modern-primary:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 6px 18px rgba(41,90,167,0.4); color: white;
                }
                .btn-modern-danger {
                    background: linear-gradient(135deg, var(--danger), #dc2626);
                    box-shadow: 0 4px 12px rgba(239,68,68,0.3);
                }
                .btn-modern-danger:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 6px 18px rgba(239,68,68,0.4); color: white;
                }
                .btn-modern-warning {
                    background: linear-gradient(135deg, var(--warning), #d97706);
                    box-shadow: 0 4px 12px rgba(245,158,11,0.3);
                }
                .btn-modern-warning:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 6px 18px rgba(245,158,11,0.4); color: white;
                }
                .btn-modern-outline:disabled,
                .btn-modern-primary:disabled,
                .btn-modern-danger:disabled,
                .btn-modern-warning:disabled { opacity: 0.6; transform: none; }

                /* ===== ALERTES ===== */
                .alert-error-charte {
                    border: none; background: #fef2f2;
                    border-left: 4px solid var(--danger);
                    border-radius: 16px; padding: 1rem 1.5rem;
                    box-shadow: 0 8px 32px rgba(239,68,68,0.08);
                }
                .alert-error-icon {
                    width: 48px; height: 48px; border-radius: 12px;
                    background: rgba(239,68,68,0.1);
                    display: flex; align-items: center; justify-content: center;
                    color: var(--danger); font-size: 1.5rem;
                }

                /* ===== LOADING ===== */
                .loading-spinner-charte { position: relative; width: 80px; height: 80px; margin: 0 auto; }
                .loading-ring-charte {
                    position: absolute; inset: 0; border-radius: 50%;
                    border: 4px solid #e2e8f0; border-top-color: var(--primary);
                    animation: spin 1s linear infinite;
                }
                .loading-icon-charte {
                    position: absolute; top: 50%; left: 50%;
                    transform: translate(-50%, -50%);
                    font-size: 28px; color: var(--primary);
                }

                /* ===== TOAST ===== */
                .toast-notification-fixed {
                    position: fixed; bottom: 24px; right: 24px;
                    z-index: 9999; animation: slideInRight 0.3s ease-out;
                }
                .toast-modern {
                    background: white; border-radius: 16px;
                    box-shadow: 0 20px 40px rgba(0,0,0,0.15);
                    min-width: 320px; max-width: 400px; overflow: hidden;
                }
                .toast-header-custom {
                    padding: 12px 20px;
                    display: flex; align-items: center; justify-content: space-between;
                }
                .toast-close-btn {
                    background: transparent; border: none;
                    color: white; cursor: pointer; opacity: 0.8;
                }
                .toast-close-btn:hover { opacity: 1; }
                .toast-body-custom { padding: 16px 20px; }
                .toast-body-custom p { color: #1e293b; font-size: 0.9rem; }
                .toast-progress { height: 3px; width: 100%; animation: shrink linear forwards; }

                /* ===== ANIMATIONS ===== */
                @keyframes spin { to { transform: rotate(360deg); } }
                @keyframes slideInRight {
                    from { opacity: 0; transform: translateX(100%); }
                    to { opacity: 1; transform: translateX(0); }
                }
                @keyframes shrink {
                    from { width: 100%; }
                    to { width: 0%; }
                }

                /* ===== RESPONSIVE ===== */
                @media (max-width: 768px) {
                    .header-modern { padding: 1rem; }
                    .header-icon-wrapper { width: 44px; height: 44px; font-size: 1.2rem; }
                    .header-title { font-size: 1.1rem; }
                    .pagination-wrapper-charte { flex-direction: column; align-items: stretch; }
                    .pagination-info-charte { text-align: center; }
                    .toast-notification-fixed { bottom: 12px; right: 12px; left: 12px; }
                    .toast-modern { min-width: auto; max-width: none; }
                }
                .rounded-3 { border-radius: 12px !important; }
            `}</style>
        </>
    );
}